/**
 * Created by danielg on 9/12/15.
 */
define(function (require) {

    window.lpTag = window.lpTag || {};
    window.lpTag.WebAgent = window.lpTag.WebAgent || {};

    var webAgentStatics = require('web-agent-assets-js/lpWebAgentStatics');
    var BaseController = require('web-agent-assets-js/BaseController');
    var WebAgentLogger = require('web-agent-assets-js/WebAgentLogger');
    var QueueMessagingAdapter = require('web-agent-adapters/QueueMessagingAdapter');
    var QueueRealTimeAdapter = require('web-agent-adapters/QueueRealTimeAdapter');

    /////////////////////////////////// PRIVATE VARIABLES ///////////////////////////////////
    var _queueModel;
    var _logger;
    var _messagingAdapter;
    var _realTimeAdapter;
    var _channel = webAgentStatics.COMMUNICATION_CHANNELS.QUEUE_ORB;

    /////////////////////////////////// INITIALIZATION ///////////////////////////////////
    var QueueOrbAdapter = BaseController.extend(function QueueOrbAdapter(options) {
        _logger = this.logger || WebAgentLogger.getLogger(this.applicationType);
        if (options) {
            _setModel(options.model);
        }
        if (_queueModel) {
            // Set binds here
            _messagingAdapter = QueueMessagingAdapter.getInstance({
                model: _queueModel,
                context: this,
                isConnected: _onIsConnected,
                countChange: _onCountChange,
                timerChange: _onTimerChange,
                autoAccepting: _onAutoAccepting,
                acceptSuccess: _onAcceptSuccess,
                acceptError: _onAcceptError,
                maxChatsChange: _onMaxChatsReached
            });
            _realTimeAdapter = QueueRealTimeAdapter.getInstance({
                model: _queueModel,
                context: this,
                isConnected: _onIsConnected,
                countChange: _onCountChange,
                timerChange: _onTimerChange,
                autoAccepting: _onAutoAccepting,
                acceptSuccess: _onAcceptSuccess,
                acceptError: _onAcceptError,
                maxChatsChange: _onMaxChatsReached
            });
        }
    });

    /////////////////////////////////// PUBLIC VARIABLES ///////////////////////////////////
    QueueOrbAdapter.prototype.applicationType = 'QueueOrbAdapter';
    QueueOrbAdapter.prototype.ringingCount = 0;
    QueueOrbAdapter.prototype.maxChatsReached = false;
    QueueOrbAdapter.prototype.isConnected = false;
    QueueOrbAdapter.prototype.isAutoAcceptEnabled = false;
    QueueOrbAdapter.prototype.isTimerEnabled = false;

    /////////////////////////////////// PUBLIC METHODS ///////////////////////////////////
    /**
     * Returns true if there are chats in the queue and the service is connected and max chats is not reached
     * @returns {boolean} True if there are chats in the queue and the service is connected and max chats is not reached
     */
    QueueOrbAdapter.prototype.canAcceptChat = function () {
        return this.hasChatsInQueue() && !this.maxChatsReached;
    };

    /**
     * Returns true if there are chats in the queue and the service is connected
     * @returns {boolean} True if there are chats in the queue and the service is connected
     */
    QueueOrbAdapter.prototype.hasChatsInQueue = function () {
        return this.ringingCount > 0 && this.isConnected;
    };

    /**
     * Accepts the first chat in the queue, if there are chats in the queue
     */
    QueueOrbAdapter.prototype.acceptChat = function () {
        if (_realTimeAdapter.canAcceptChat()) {
            _realTimeAdapter.acceptChat();
        } else if (_messagingAdapter.canAcceptChat()) {
            _messagingAdapter.acceptChat();
        }
    };

    /////////////////////////////////// PRIVATE METHODS ///////////////////////////////////
    /**
     * Sets the model
     * @param model - the model
     * @private
     */
    function _setModel(model) {
        if (model) {
            _queueModel = model;
        }
    }

    /**
     * Called when there is a change in the connection state of real time or messaging.
     * Updates all properties/events which rely on the connection state.
     * @private
     */
    function _onIsConnected() {
        _updateTimer.call(this);
        _updateMaxChatsReached.call(this);
        _updateIsConnected.call(this);
        _updateRingingCount.call(this);
    }

    /**
     * Called when the ringing count changes for real time or messaging.
     * Updates all properties/events which rely on the ringing count.
     * @private
     */
    function _onCountChange() {
        _updateMaxChatsReached.call(this);
        _updateTimer.call(this);
        _updateRingingCount.call(this);
    }

    /**
     * Called when there is a change in the ACD timer for real time or messaging.
     * Updates the timer.
     * @private
     */
    function _onTimerChange() {
        _updateTimer.call(this);
    }

    /**
     * Called when we attempt to auto accept a chat in real time or messaging.
     * Fires an autoAccepting event.
     * @private
     */
    function _onAutoAccepting() {
        _fireEvent(_channel.events.autoAccepting, {});
    }

    /**
     * Called when we have successfully accepted a chat in real time or messaging.
     * Fires an acceptSuccess event.
     * @param data - The data of the accept success event
     * @private
     */
    function _onAcceptSuccess(data) {
        _fireEvent(_channel.events.acceptSuccess, data || {});
    }

    /**
     * Called when we have encountered an error when attempting to accept a chat in real time or messaging.
     * Fires an acceptError event.
     * @private
     */
    function _onAcceptError() {
        _fireEvent(_channel.events.acceptError, {});
    }

    /**
     * Called when there is a change in max chats reached in real time or messaging.
     * Updates the max chats reached.
     * @private
     */
    function _onMaxChatsReached() {
        _updateMaxChatsReached.call(this);
    }

    /**
     * Updates the isConnected property, and fires a connectionStateChange event if it has changed.
     * We consider ourselves connected if either real time or messaging are connected.
     * @private
     */
    function _updateIsConnected() {
        var oldValue = this.isConnected;
        this.isConnected = _realTimeAdapter.isConnected || _messagingAdapter.isConnected;
        if (oldValue !== this.isConnected) {
            _fireEvent(_channel.events.connectionStateChange, this.isConnected);
        }
    }

    /**
     * Updates the ringingCount property, and fires a ringCountChange event if it has changed.
     * We consider the ringing count to be the sum of the real time and messaging ringing counts, if they're connected.
     * @private
     */
    function _updateRingingCount() {
        var oldValue = this.ringingCount;
        var realTimeCount = 0;
        var messagingCount = 0;
        if (_messagingAdapter.isConnected) {
            messagingCount += _messagingAdapter.ringingCount;
        }
        if (_realTimeAdapter.isConnected) {
            realTimeCount += _realTimeAdapter.ringingCount;
        }
        this.ringingCount = realTimeCount + messagingCount;
        if (oldValue !== this.ringingCount) {
            _fireEvent(_channel.events.ringCountChange, {count: this.ringingCount, realTime: realTimeCount, messaging: messagingCount});
        }
    }

    /**
     * Updates the timer (isTimerEnabled and awayTimeOut properties), and fires a timerChange event if there is a change.
     * Since we accept real timer chats first, the timer is enabled if real time timer is enabled and we can accept
     * real time chats, or if messaging timer is enabled and we can't accept real time chats. Obviously, if they are
     * both enabled - the timer is enabled.
     * @private
     */
    function _updateTimer() {
        var oldEnabledValue = this.isTimerEnabled;
        var oldTimeoutValue = this.awayTimeOut;
        this.isTimerEnabled = (_realTimeAdapter.isTimerEnabled && _messagingAdapter.isTimerEnabled) ||
            (_realTimeAdapter.isTimerEnabled && _realTimeAdapter.canAcceptChat()) ||
            (_messagingAdapter.isTimerEnabled && !_realTimeAdapter.canAcceptChat());
        if (this.isTimerEnabled) {
            this.awayTimeOut = _realTimeAdapter.awayTimeOut || _messagingAdapter.awayTimeOut;
        } else {
            this.awayTimeOut = undefined;
        }
        if (oldEnabledValue !== this.isTimerEnabled || this.awayTimeOut !== oldTimeoutValue) {
            _fireEvent(_channel.events.timerChange, {isTimerEnabled: this.isTimerEnabled, awayTimeOut: this.awayTimeOut});
        }
    }

    /**
     * Updates the maxChatsReached property, and fires a maxChatsChange event if it changed.
     * We consider this property to be true if either real time or messaging has reached max chats, and neither can
     * accept a chat.
     * @private
     */
    function _updateMaxChatsReached() {
        var oldValue = this.maxChatsReached;
        this.maxChatsReached = ((_realTimeAdapter.maxChatsReached || _messagingAdapter.maxChatsReached) &&
        !_realTimeAdapter.canAcceptChat() && !_messagingAdapter.canAcceptChat());
        if (oldValue !== this.maxChatsReached) {
            _fireEvent(_channel.events.maxChatsChange, this.maxChatsReached);
        }
    }

    /**
     * Fires an event for the QueueOrbAdapter
     * @param eventName The name of the event
     * @param data The data of the event
     * @private
     */
    function _fireEvent(eventName, data) {
        lpTag.WebAgent.channel.publish(_channel.name, eventName, data);
    }

    return QueueOrbAdapter;

});
